import React, { useEffect, useState } from 'react';
import Login from './Login';
import Signup from './Signup';
import ResetPassword from './ResetPassword';
import Home from './Home';
import { apiGet } from './apiUtils';
import StockOnboarding from './StockOnboarding';
import ManageStock from './ManageStock';
import UploadInvoice from './UploadInvoice';
import IndentEstimate from './IndentEstimate';
import Sheets from './Sheets';
import UpdateClosingStock from './UpdateClosingStock';
import IncomeExpenses from './IncomeExpenses';
import IncomeExpensesReport from './IncomeExpensesReport';
import TrackPayments from './TrackPayments';
import DownloadSaleSheet from './DownloadSaleSheet';
import Reports from './Reports';
import StockLifted from './StockLifted';
import SalesReport from './SalesReport';
import AddStore from './AddStore';
import ShiftTransfer from './ShiftTransfer';
import StockTransferReport from './StockTransferReport';
import StockReceived from './StockReceived';
import Subscription from './Subscription';
import AccessoryManagement from './AccessoryManagement';
import ManageStockLayout from './components/ManageStockLayout';
import Navigation from './components/Navigation';
import SecondaryNav from './components/SecondaryNav';
import { Toaster } from './components/ui/sonner';
import { ClipboardList, IndianRupee, CreditCard, FileDown, PackageOpen, Tag, ArrowLeftRight } from 'lucide-react';
import { getCurrentUser } from './authUtils';
import './App.css';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';

// Main App component that uses contexts
function AppContent() {
  const { currentView, navigate } = useNavigation();
  const { isAuthenticated, loading, handleLogin, handleAuthError } = useAuthContext();
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);

  // Check subscription status when authenticated
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated) return;
      try {
        const res = await apiGet('/api/razorpay/subscription');
        const data = await res.json();
        // Use hasActiveSubscription from backend - it checks both status AND end_date
        // CANCELLED subscriptions still have access until end_date
        if (!data.hasActiveSubscription) {
          setSubscriptionRequired(true);
        } else {
          setSubscriptionRequired(false);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    };
    checkSubscription();
  }, [isAuthenticated]);

  // Force to subscription page if required
  useEffect(() => {
    if (subscriptionRequired && isAuthenticated && currentView !== 'subscription') {
      navigate('subscription');
    }
  }, [subscriptionRequired, isAuthenticated, currentView, navigate]);

  // Redirect based on authentication status (only when necessary)
  useEffect(() => {
    // Don't redirect during initial load - let NavigationContext handle URL-based routing
    if (loading) return;
    
    // Redirect to home only if user is on login/signup/resetPassword while authenticated
    if (isAuthenticated && (currentView === 'login' || currentView === 'signup' || currentView === 'resetPassword')) {
      navigate('home');
    } 
    // Redirect to login only if not authenticated and trying to access protected routes
    else if (!isAuthenticated && currentView !== 'login' && currentView !== 'signup' && currentView !== 'resetPassword') {
      navigate('login');
    }
  }, [isAuthenticated, currentView, loading, navigate]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="App">
        {currentView === 'login' && (
          <Login 
            onLogin={handleLogin} 
            onSignup={() => navigate('signup')}
            onResetPassword={() => navigate('resetPassword')}
          />
        )}
        {currentView === 'signup' && (
          <Signup 
            onLogin={() => navigate('login')} 
            onSignup={() => navigate('login')} 
          />
        )}
        {currentView === 'resetPassword' && (
          <ResetPassword 
            onBack={() => navigate('login')} 
          />
        )}
      </div>
    );
  }

  const user = getCurrentUser();
  const shopName = user?.shopName || 'Easy Sheets, Daily';

  // Determine the current page for Navigation highlight
  const getCurrentPage = () => {
    if (currentView === 'uploadInvoice' || currentView === 'indentEstimate' || 
        currentView === 'shiftTransfer' || currentView === 'addStore' || 
        currentView === 'stockReceived' || currentView === 'accessories' || currentView === 'manageStock') {
      return 'manageStock';
    }
    if (currentView === 'updateClosingStock' || currentView === 'incomeExpenses' || 
        currentView === 'trackPayments' || currentView === 'sheets') {
      return 'sheets';
    }
    if (currentView === 'downloadSaleSheet' || currentView === 'brandWiseSales' || 
        currentView === 'stockTransferReport' || currentView === 'stockLifted' || 
        currentView === 'incomeExpensesReport' || currentView === 'reports') {
      return 'reports';
    }
    return currentView;
  };

  // Determine which secondary nav to show
  const getSecondaryNavConfig = () => {
    // Only show secondary nav on sub-pages, not on main /sheets and /reports pages
    const sheetsViews = ['updateClosingStock', 'incomeExpenses', 'trackPayments'];
    const reportsViews = ['downloadSaleSheet', 'incomeExpensesReport', 'stockLifted', 'brandWiseSales', 'stockTransferReport'];
    
    if (sheetsViews.includes(currentView)) {
      return {
        show: true,
        items: [
          { view: 'updateClosingStock', label: 'Update Closing Stock', icon: ClipboardList },
          { view: 'incomeExpenses', label: 'Income/Expenses', icon: IndianRupee },
          { view: 'trackPayments', label: 'Track Payments', icon: CreditCard },
        ]
      };
    }
    
    if (reportsViews.includes(currentView)) {
      return {
        show: true,
        items: [
          { view: 'downloadSaleSheet', label: 'Download Sale Sheet', icon: FileDown },
          { view: 'incomeExpensesReport', label: 'Income & Expenses Report', icon: IndianRupee },
          { view: 'stockLifted', label: 'Stock Lifted Report', icon: PackageOpen },
          { view: 'brandWiseSales', label: 'Sales Report', icon: Tag },
          { view: 'stockTransferReport', label: 'Stock Transfer Report', icon: ArrowLeftRight },
        ]
      };
    }
    
    return { show: false };
  };

  const secondaryNavConfig = getSecondaryNavConfig();

  return (
    <UserProvider isAuthenticated={isAuthenticated} onAuthError={handleAuthError}>
      <Toaster position="bottom-right" />
      <div className="App">
        <Navigation 
          currentPage={getCurrentPage()}
          shopName={shopName}
        />
        
        {secondaryNavConfig.show && (
          <SecondaryNav 
            items={secondaryNavConfig.items}
            currentView={currentView}
          />
        )}
        
        {currentView === 'home' && <Home />}
        {currentView === 'stockOnboarding' && (
          <StockOnboarding isAuthenticated={isAuthenticated} />
        )}
        {currentView === 'manageStock' && <ManageStock />}
        {(currentView === 'uploadInvoice' || currentView === 'indentEstimate' || 
          currentView === 'shiftTransfer' || currentView === 'addStore' || 
          currentView === 'stockReceived' || currentView === 'accessories') && (
          <ManageStockLayout currentView={currentView}>
            {currentView === 'uploadInvoice' && <UploadInvoice />}
            {currentView === 'indentEstimate' && <IndentEstimate />}
            {currentView === 'shiftTransfer' && <ShiftTransfer />}
            {currentView === 'addStore' && <AddStore />}
            {currentView === 'stockReceived' && <StockReceived />}
            {currentView === 'accessories' && <AccessoryManagement />}
          </ManageStockLayout>
        )}
        {currentView === 'sheets' && <Sheets />}
        {currentView === 'updateClosingStock' && <UpdateClosingStock />}
        {currentView === 'incomeExpenses' && <IncomeExpenses />}
        {currentView === 'incomeExpensesReport' && <IncomeExpensesReport />}
        {currentView === 'trackPayments' && <TrackPayments />}
        {currentView === 'downloadSaleSheet' && <DownloadSaleSheet />}
        {currentView === 'reports' && <Reports />}
        {currentView === 'stockLifted' && <StockLifted />}
        {currentView === 'brandWiseSales' && <SalesReport />}
        {currentView === 'stockTransferReport' && <StockTransferReport />}
        {currentView === 'subscription' && <Subscription />}
      </div>
    </UserProvider>
  );
}

// Intermediate component to connect Auth and Navigation contexts
function AppWithNavigation() {
  const { handleLogout } = useAuthContext();
  return (
    <NavigationProvider onLogout={handleLogout}>
      <AppContent />
    </NavigationProvider>
  );
}

// Root App component with context providers
function App() {
  return (
    <AuthProvider>
      <AppWithNavigation />
    </AuthProvider>
  );
}

export default App;